/**
 * RESPONSIBILITY: the sermons list — search, filters, and the cards they leave.
 *
 *   [ Search…………………… ][ All series v ][ All speakers v ][ All years v ][ All special events v ]
 *   [ card ][ card ][ card ]
 *
 * Untouched, it shows the newest few (the section says how many): the page is for last
 * Sunday's sermon, not an archive to scroll. Any search or filter searches ALL of them,
 * newest first — that is how an older one is found (R66).
 *
 * Each filter offers only values some shown sermon has, so no choice leads to nothing.
 *
 * THE SEARCH BOX IS ALWAYS THERE. YouTube's newest videos not yet entered as sermons are
 * searched too, by title — their titles already carry the speaker and the scripture — so
 * the box works from the first day, before a single sermon has been entered. The other
 * filters appear as the entered sermons give them something to offer.
 */
import { useMemo, useState } from 'react';
import { idOf } from '@framework/core';
import type { SermonRow, SermonsSection } from '@app/sections';
import { FilterPick } from '../commons/FilterPick';
import { useLatestSermons, youtubeId, VideoPlayer, YoutubeThumb } from '../commons';
import { SermonCard } from './SermonCard';
import {
  NO_FILTERS, isFiltered, matches, newestFirst, used, yearOf, onlySearch, titleMatches,
  type SermonFilters,
} from './sermon-filter';

export function SermonFinder({ c, sermons }: { c: SermonsSection; sermons: readonly SermonRow[] }) {
  const [f, setF] = useState<SermonFilters>(NO_FILTERS);
  const set = (patch: Partial<SermonFilters>) => setF((x) => ({ ...x, ...patch }));

  const all = useMemo(() => [...sermons].sort(newestFirst), [sermons]);
  const shown = useMemo(
    () => (isFiltered(f) ? all.filter((s) => matches(s, f)) : all.slice(0, c.latestCount || 12)),
    [all, f, c.latestCount],
  );

  // In the order the section keeps them — the order the admin chose — limited to used ones.
  const inOrder = (list: string[] | undefined, pick: (s: SermonRow) => string | undefined) => {
    const have = new Set(used(all, pick));
    return (list ?? []).filter((v) => have.has(v));
  };
  const years = used(all, yearOf).sort().reverse();

  // YouTube's newest not yet entered: the list itself while nothing is entered, and
  // searched by title whenever the search is the only filter in use.
  const entered = new Set(all.map((s) => youtubeId(s.youtubeUrl)).filter(Boolean));
  const fresh = useLatestSermons().filter((v) => !entered.has(v.videoId));
  const live = !isFiltered(f) ? (all.length ? [] : fresh)
    : onlySearch(f) ? fresh.filter((v) => titleMatches(v.title, f.q)) : [];

  if (all.length === 0 && fresh.length === 0) return null;

  return (
    <section className="band">
      <div className="wrap">
        {c.latestHeading && <div className="band-head"><h2>{c.latestHeading}</h2></div>}

        <div className="sermon-filters" role="search">
          <input
            type="search" className="sermon-search" value={f.q} aria-label={c.searchLabel}
            placeholder={c.searchLabel} onChange={(e) => set({ q: e.target.value })}
          />
          <FilterPick allLabel={c.allSeries} options={inOrder(c.series, (s) => s.series)}
                      picked={f.series} onChange={(series) => set({ series })} />
          <FilterPick allLabel={c.allSpeakers} options={inOrder(c.speakers, (s) => s.speaker)}
                      picked={f.speakers} onChange={(speakers) => set({ speakers })} />
          <FilterPick allLabel={c.allYears} options={years}
                      picked={f.years} onChange={(ys) => set({ years: ys })} />
          <FilterPick allLabel={c.allOccasions} options={inOrder(c.occasions, (s) => s.occasion)}
                      picked={f.occasions} onChange={(occasions) => set({ occasions })} />
          {isFiltered(f) && c.clearLabel && (
            <button type="button" className="sermon-clear" onClick={() => setF(NO_FILTERS)}>
              {c.clearLabel}
            </button>
          )}
        </div>

        {shown.length + live.length === 0
          ? c.noMatch && <p className="sermon-none" role="status">{c.noMatch}</p>
          : (
            <div className="sermon-grid">
              {shown.map((s) => <SermonCard key={idOf(s)} s={s} />)}
              {live.map((v) => (
                <div className="sermon-live" key={v.videoId}>
                  <VideoPlayer url={v.url} title={v.title} className="sermon-video"
                               picture={<YoutubeThumb url={v.url} alt={v.title} />} />
                  <h3 className="sermon-title">{v.title}</h3>
                </div>
              ))}
            </div>
          )}
      </div>
    </section>
  );
}
